import React, { useState } from 'react';
import {
   Box,
   Typography,
   Button,
   Container,
   Grid,
   Card,
   CardContent,
   AppBar,
   Toolbar,
   IconButton,
   useTheme,
   useMediaQuery,
   keyframes,
   alpha,
} from '@mui/material';
import {
   Favorite as HeartIcon,
   Groups as UsersIcon,
   AutoAwesome as SparklesIcon,
   Star as StarIcon,
   ArrowForward as ArrowRightIcon,
   Security as ShieldIcon,
   Public as GlobeIcon,
   Adjust as TargetIcon,
   Rocket as RocketIcon,
   Schedule as ClockIcon,
   EmojiEvents as AwardIcon,
   CheckCircle as CheckCircleIcon,
   Chat as MessageCircleIcon,
   PhotoCamera as CameraIcon,
   Event as CalendarIcon,
   Home as HomeIcon,
   Menu as MenuIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';

// Animations matching landing page
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-8px) rotate(1deg); }
  66% { transform: translateY(-4px) rotate(-1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const About: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const navigate = useNavigate();
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         {/* Navigation */}
         <AppBar
            position='fixed'
            sx={{
               background:
                  'linear-gradient(135deg, rgba(0, 196, 167, 0.95) 0%, rgba(0, 67, 105, 0.95) 100%)',
               backdropFilter: 'blur(15px)',
               boxShadow: '0 2px 15px rgba(0, 196, 167, 0.2)',
               zIndex: 1100,
               color: 'white',
               border: 'none',
               borderRadius: 0,
            }}
         >
            <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
               <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => navigate('/')}
               >
                  <img
                     src={logo}
                     alt='WeTrippen Logo'
                     style={{ width: 32, height: 32, borderRadius: 6 }}
                  />
                  <Typography
                     variant='h6'
                     sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}
                  >
                     WeTrippen
                  </Typography>
               </Box>

               {!isMobile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                     <Button
                        component={RouterLink}
                        to='/login'
                        sx={{
                           color: 'rgba(255,255,255,0.9)',
                           textTransform: 'none',
                           fontSize: '0.9rem',
                           fontWeight: 400,
                        }}
                     >
                        Sign In
                     </Button>
                     <Button
                        component={RouterLink}
                        to='/signup'
                        variant='contained'
                        sx={{
                           background: 'rgba(255,255,255,0.2)',
                           color: 'white',
                           textTransform: 'none',
                           fontWeight: 500,
                           borderRadius: 2,
                           px: 2.5,
                           py: 0.5,
                           fontSize: '0.9rem',
                           backdropFilter: 'blur(10px)',
                           border: '1px solid rgba(255,255,255,0.3)',
                           '&:hover': {
                              background: 'rgba(255,255,255,0.3)',
                              transform: 'translateY(-1px)',
                           },
                        }}
                     >
                        Get Started
                     </Button>
                  </Box>
               ) : (
                  <IconButton
                     onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                     sx={{ color: 'white' }}
                  >
                     {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
               )}
            </Toolbar>

            {/* Mobile Menu */}
            {isMobile && mobileMenuOpen && (
               <Box sx={{ bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/login'
                     sx={{ justifyContent: 'flex-start', mb: 1, color: 'text.primary' }}
                  >
                     Sign In
                  </Button>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/signup'
                     variant='contained'
                     sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                     Get Started
                  </Button>
               </Box>
            )}
         </AppBar>

         {/* Hero Section */}
         <Box
            sx={{
               pt: 20,
               pb: 10,
               background:
                  'linear-gradient(135deg, rgba(0, 196, 167, 0.1) 0%, rgba(0, 67, 105, 0.1) 50%, rgba(255, 107, 107, 0.1) 100%)',
            }}
         >
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center' }}>
                  <Box
                     sx={{
                        width: 80,
                        height: 80,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 4,
                        animation: `${float} 3s ease-in-out infinite`,
                     }}
                  >
                     <HeartIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                  <Typography
                     variant='h2'
                     sx={{
                        fontWeight: 700,
                        mb: 3,
                        background: theme.palette.gradient?.primary,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                     }}
                  >
                     We Believe People Are Better Together
                  </Typography>
                  <Typography
                     variant='h6'
                     sx={{
                        color: 'text.secondary',
                        mb: 4,
                        maxWidth: 600,
                        mx: 'auto',
                        lineHeight: 1.6,
                     }}
                  >
                     WeTrippen exists because human connection shouldn't be left to chance. We're
                     here to help you build the communities you've always imagined.
                  </Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                     Why We Built WeTrippen: A Letter to Community Builders
                  </Typography>
               </Box>
            </Container>
         </Box>

         {/* The Story Behind WeTrippen */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     The Story Behind WeTrippen
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600, mb: 4 }}>
                     "Connection is becoming a lost art."
                  </Typography>
               </Box>

               <Box
                  sx={{
                     '& p': { mb: 3, fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 },
                  }}
               >
                  <Typography component='p'>
                     We live in the most connected era in human history, yet loneliness rates have
                     never been higher. People swipe through hundreds of faces but struggle to make
                     a single meaningful friend. Neighbors live side by side for years without
                     knowing each other's names. Families gather once a year and spend the time
                     staring at phones.
                  </Typography>

                  <Typography component='p'>
                     But here's what we've learned: people desperately want to connect. They want to
                     belong to something bigger than themselves. They want to create memories that
                     matter. They want to be part of communities that celebrate life's moments
                     together.
                  </Typography>

                  <Typography component='p' sx={{ fontWeight: 500 }}>
                     The problem isn't desire - it's opportunity. And that's exactly why WeTrippen
                     exists.
                  </Typography>
               </Box>
            </Container>
         </Box>

         {/* Our Mission */}
         <Box sx={{ py: 10, bgcolor: 'grey.50' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                     }}
                  >
                     <TargetIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Our Mission
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600, mb: 4 }}>
                     "Every Person Deserves a Community That Celebrates Them"
                  </Typography>
               </Box>

               <Card
                  sx={{ p: { xs: 4, md: 6 }, boxShadow: '0 8px 30px rgba(0, 196, 167, 0.15)' }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <Typography
                        variant='body1'
                        sx={{ fontSize: '1.1rem', color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                     >
                        We built WeTrippen because we believe organizing people shouldn't be a
                        superpower reserved for the naturally gifted few. Whether you're a shy
                        introvert who dreams of hosting dinner parties or a natural connector who
                        wants to bring your whole neighborhood together, you deserve tools that make
                        community building feel effortless and joyful.
                     </Typography>

                     <Box
                        sx={{
                           bgcolor: alpha(theme.palette.primary.main, 0.05),
                           p: 3,
                           borderRadius: 2,
                        }}
                     >
                        <Typography
                           variant='h6'
                           sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}
                        >
                           Our mission is simple: Remove every barrier between you and the
                           connections you crave.
                        </Typography>
                     </Box>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* The Problem We're Solving */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     The Problem We're Solving
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600 }}>
                     "Why Bringing People Together Feels So Hard"
                  </Typography>
               </Box>

               <Grid container spacing={3} sx={{ mb: 6 }}>
                  {[
                     {
                        icon: <UsersIcon />,
                        title: "Sarah's Book Club",
                        description:
                           'Sarah wants to host a book club but gets overwhelmed coordinating schedules, managing RSVPs, and keeping discussions going between meetings. After three months, attendance dwindles and the group quietly dissolves.',
                        color: 'error',
                     },
                     {
                        icon: <GlobeIcon />,
                        title: "Marcus's Adventures",
                        description:
                           'Marcus organizes incredible hiking adventures but watches as group photos disappear into phones and new friendships fade when the trail ends. He dreams of a hiking community but ends up planning each trip from scratch.',
                        color: 'success',
                     },
                     {
                        icon: <HeartIcon />,
                        title: 'Johnson Family Reunions',
                        description:
                           'The Johnson family plans reunions that bring 60 relatives together for one beautiful weekend, then everyone scatters for three years until someone has to start organizing all over again.',
                        color: 'info',
                     },
                     {
                        icon: <CameraIcon />,
                        title: 'Photography Enthusiasts',
                        description:
                           'Local photography enthusiasts meet monthly but struggle to move beyond surface-level conversations. Great connections happen at events but have nowhere to grow.',
                        color: 'secondary',
                     },
                  ].map((story, index) => (
                     <Grid item xs={12} sm={6} key={index}>
                        <Card
                           sx={{
                              p: 3,
                              height: '100%',
                              boxShadow: '0 4px 20px rgba(0, 196, 167, 0.1)',
                           }}
                        >
                           <CardContent sx={{ p: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                 <Box
                                    sx={{
                                       width: 48,
                                       height: 48,
                                       bgcolor: `${story.color}.light`,
                                       color: `${story.color}.main`,
                                       borderRadius: '50%',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       mr: 2,
                                    }}
                                 >
                                    {story.icon}
                                 </Box>
                                 <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                    {story.title}
                                 </Typography>
                              </Box>
                              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                 {story.description}
                              </Typography>
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>

               <Box sx={{ textAlign: 'center' }}>
                  <Typography
                     variant='body1'
                     sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}
                  >
                     These aren't stories about failure - they're stories about people who care
                     deeply about connection but lack the right tools to nurture it.
                  </Typography>
               </Box>
            </Container>
         </Box>

         {/* How WeTrippen Changes Everything */}
         <Box
            sx={{
               py: 10,
               background:
                  'linear-gradient(135deg, rgba(0, 196, 167, 0.05) 0%, rgba(0, 67, 105, 0.05) 100%)',
            }}
         >
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                     }}
                  >
                     <SparklesIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     How WeTrippen Changes Everything
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600 }}>
                     "What Happens When Technology Serves Human Connection"
                  </Typography>
               </Box>

               <Card
                  sx={{
                     p: { xs: 4, md: 6 },
                     boxShadow: '0 8px 30px rgba(0, 196, 167, 0.15)',
                     mb: 6,
                  }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <Typography
                        variant='h6'
                        sx={{ textAlign: 'center', fontWeight: 600, color: 'primary.main', mb: 4 }}
                     >
                        WeTrippen transforms the exhausting work of community building into the
                        joyful art of bringing people together.
                     </Typography>

                     <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    title: 'Instead of scattered group chats that die',
                                    desc: 'You get permanent spaces where relationships deepen over time',
                                 },
                                 {
                                    title: 'Instead of photos buried in phones',
                                    desc: 'You get beautiful shared albums that keep memories alive and bring people back together',
                                 },
                              ].map((item, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <CheckCircleIcon
                                       sx={{ color: 'success.main', mt: 0.5, fontSize: 20 }}
                                    />
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {item.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {item.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    title: 'Instead of starting from scratch every time',
                                    desc: 'You build on every past connection to create richer future experiences',
                                 },
                                 {
                                    title: 'Instead of losing touch between events',
                                    desc: 'You maintain the bonds that make communities thrive',
                                 },
                              ].map((item, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <CheckCircleIcon
                                       sx={{ color: 'success.main', mt: 0.5, fontSize: 20 }}
                                    />
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {item.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {item.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>
                     </Grid>

                     <Box
                        sx={{
                           mt: 4,
                           pt: 4,
                           borderTop: '1px solid',
                           borderColor: 'divider',
                           textAlign: 'center',
                        }}
                     >
                        <Typography variant='body1' sx={{ fontWeight: 500, color: 'primary.main' }}>
                           We handle the logistics so you can focus on what matters: creating
                           moments that matter with people who matter to you.
                        </Typography>
                     </Box>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* Our Deeper Belief */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                     }}
                  >
                     <GlobeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Our Deeper Belief
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600 }}>
                     "Organized Communities Change the World"
                  </Typography>
               </Box>

               <Grid container spacing={3}>
                  {[
                     {
                        icon: <UsersIcon />,
                        title: 'Children grow up knowing',
                        desc: 'they belong to something bigger than their immediate family',
                        color: 'info',
                     },
                     {
                        icon: <ShieldIcon />,
                        title: 'Adults find support systems',
                        desc: 'that help them through challenges and celebrate victories',
                        color: 'success',
                     },
                     {
                        icon: <HomeIcon />,
                        title: 'Neighborhoods become places',
                        desc: "where people know each other's names and look out for one another",
                        color: 'secondary',
                     },
                     {
                        icon: <HeartIcon />,
                        title: 'Shared interests become',
                        desc: 'launching pads for lifelong friendships and collaborative projects',
                        color: 'warning',
                     },
                     {
                        icon: <StarIcon />,
                        title: 'Traditions get passed down',
                        desc: 'because communities exist to carry them forward',
                        color: 'error',
                     },
                     {
                        icon: <SparklesIcon />,
                        title: 'Innovation happens',
                        desc: 'when diverse groups regularly share ideas and build together',
                        color: 'info',
                     },
                  ].map((belief, index) => (
                     <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                           sx={{
                              p: 3,
                              textAlign: 'center',
                              height: '100%',
                              boxShadow: '0 4px 20px rgba(0, 196, 167, 0.1)',
                              '&:hover': { boxShadow: '0 8px 30px rgba(0, 196, 167, 0.15)' },
                           }}
                        >
                           <CardContent sx={{ p: 0 }}>
                              <Box
                                 sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: `${belief.color}.light`,
                                    color: `${belief.color}.main`,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2,
                                 }}
                              >
                                 {belief.icon}
                              </Box>
                              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                 {belief.title}
                              </Typography>
                              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                 {belief.desc}
                              </Typography>
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>

               <Box sx={{ mt: 6, textAlign: 'center' }}>
                  <Typography
                     variant='body1'
                     sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}
                  >
                     This isn't just about event planning or photo sharing. This is about rebuilding
                     the social fabric that makes life meaningful.
                  </Typography>
               </Box>
            </Container>
         </Box>

         {/* Why This Matters Now */}
         <Box sx={{ py: 10, bgcolor: 'grey.50' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                     }}
                  >
                     <ClockIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Why This Matters Now
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600 }}>
                     "The Moment for Community Building is Now"
                  </Typography>
               </Box>

               <Card
                  sx={{ p: { xs: 4, md: 6 }, boxShadow: '0 8px 30px rgba(0, 196, 167, 0.15)' }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           gap: 3,
                           '& p': { fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7 },
                        }}
                     >
                        <Typography component='p'>
                           We're living through a loneliness epidemic that touches every
                           demographic. Young adults struggle to make friends after college.
                           Families drift apart across distances. Remote work has eliminated natural
                           community spaces. Social media promised connection but delivered
                           isolation.
                        </Typography>

                        <Typography
                           component='p'
                           sx={{ color: 'primary.main', fontWeight: 600, fontSize: '1.2rem' }}
                        >
                           But we're also living through a renaissance of people who refuse to
                           accept this reality.
                        </Typography>

                        <Typography component='p'>
                           People are hosting dinner parties in their apartments. Neighbors are
                           organizing block parties. Colleagues are creating hiking groups. Families
                           are planning multi-generational adventures. Local enthusiasts are
                           building thriving communities around shared passions.
                        </Typography>

                        <Box
                           sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              p: 3,
                              borderRadius: 2,
                           }}
                        >
                           <Typography
                              sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}
                           >
                              These community builders are the heroes of our time. They're the ones
                              brave enough to send the first text, plan the first gathering, and
                              invite people into something beautiful.
                           </Typography>
                        </Box>

                        <Typography component='p' sx={{ textAlign: 'center', fontWeight: 500 }}>
                           WeTrippen exists to support these heroes. To give them tools worthy of
                           their vision. To help them turn single events into lasting communities.
                        </Typography>
                     </Box>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* Our Promise */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                     }}
                  >
                     <ShieldIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Our Promise to You
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'primary.main', fontWeight: 600 }}>
                     "We're Here for the Long Game"
                  </Typography>
               </Box>

               <Card
                  sx={{
                     p: { xs: 4, md: 6 },
                     boxShadow: '0 8px 30px rgba(0, 196, 167, 0.15)',
                     mb: 4,
                  }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <Typography
                        variant='body1'
                        sx={{
                           fontSize: '1.1rem',
                           color: 'text.secondary',
                           mb: 4,
                           textAlign: 'center',
                           lineHeight: 1.7,
                        }}
                     >
                        Building meaningful communities takes time. Real relationships develop over
                        months and years, not days and weeks. That's why WeTrippen is designed for
                        people who believe in the long game of human connection.
                     </Typography>

                     <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    icon: <AwardIcon />,
                                    title: 'Never sell your data',
                                    desc: 'Your connections are sacred, not commodities',
                                 },
                                 {
                                    icon: <RocketIcon />,
                                    title: 'Keep building better tools',
                                    desc: 'Every feature serves deeper human connection',
                                 },
                              ].map((promise, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                                       {promise.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {promise.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {promise.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    icon: <UsersIcon />,
                                    title: 'Support all community sizes',
                                    desc: 'From intimate friend groups to large networks',
                                 },
                                 {
                                    icon: <TargetIcon />,
                                    title: 'Stay focused on what matters',
                                    desc: "Helping people create the communities they've dreamed of",
                                 },
                              ].map((promise, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                                       {promise.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {promise.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {promise.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>
                     </Grid>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* Join the Movement */}
         <Box
            sx={{
               py: 10,
               background: theme.palette.gradient?.primary,
               color: 'white',
               textAlign: 'center',
            }}
         >
            <Container maxWidth='md'>
               <Box
                  sx={{
                     width: 80,
                     height: 80,
                     bgcolor: alpha('#fff', 0.2),
                     borderRadius: '50%',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     mx: 'auto',
                     mb: 4,
                  }}
               >
                  <RocketIcon sx={{ fontSize: 40, color: 'white' }} />
               </Box>
               <Typography variant='h2' sx={{ fontWeight: 700, mb: 3, color: 'white' }}>
                  Ready to Build Something Beautiful?
               </Typography>
               <Typography variant='h6' sx={{ mb: 4, color: alpha('#fff', 0.9) }}>
                  Your first community is just one event away.
               </Typography>
               <Typography
                  variant='body1'
                  sx={{ mb: 6, color: alpha('#fff', 0.9), maxWidth: 600, mx: 'auto' }}
               >
                  Join thousands of organizers who've discovered what happens when you stop hoping
                  for community and start building it.
               </Typography>

               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: { xs: 'column', sm: 'row' },
                     gap: 2,
                     justifyContent: 'center',
                     mb: 6,
                  }}
               >
                  <Button
                     component={RouterLink}
                     to='/signup'
                     size='large'
                     sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                           bgcolor: 'grey.100',
                        },
                     }}
                  >
                     Get WeTrippen Today - $35/year
                  </Button>
                  <Button
                     size='large'
                     variant='outlined'
                     sx={{
                        borderColor: 'white',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        '&:hover': {
                           bgcolor: alpha('#fff', 0.1),
                           borderColor: 'white',
                        },
                     }}
                  >
                     Everything You Need to Build Community
                  </Button>
               </Box>

               <Box
                  sx={{
                     bgcolor: alpha('#fff', 0.1),
                     backdropFilter: 'blur(10px)',
                     p: 4,
                     borderRadius: 2,
                  }}
               >
                  <Typography variant='body1' sx={{ fontWeight: 500, mb: 2 }}>
                     "We built WeTrippen because we believe every person deserves to be part of
                     communities that celebrate them, support them, and help them create memories
                     that last a lifetime."
                  </Typography>
                  <Typography variant='body2' sx={{ color: alpha('#fff', 0.9) }}>
                     Thank you for being part of this mission.
                  </Typography>
                  <Typography
                     variant='caption'
                     sx={{ color: alpha('#fff', 0.8), mt: 1, display: 'block' }}
                  >
                     - The WeTrippen Team
                  </Typography>
               </Box>
            </Container>
         </Box>
      </Box>
   );
};

export default About;
